import React, { useState } from 'react';
import { Code, Zap, Smartphone, Copy, Check, Settings, BookOpen, ArrowRight, ChevronDown, HelpCircle } from 'lucide-react';
import ChatWidget from '../components/ChatWidget/ChatWidget';
import botIcon from '../assets/iconChat.png';
import '../App.css';

export default function LandingPage() {
  const [copiedQuick, setCopiedQuick] = useState(false);
  const [copiedAdvanced, setCopiedAdvanced] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const currentOrigin = window.location.origin;

  const faqs = [
    {
      q: "How do I get my Client ID?",
      a: "Your Client ID is provided when you register your project on the RMT-AI dashboard. If you don't have one yet, contact our team to get started."
    },
    {
      q: "Does the widget slow down my website?",
      a: "No. The widget loads asynchronously using the async attribute, meaning it won't block your page from rendering. It has zero impact on your Core Web Vitals."
    },
    {
      q: "Can I customize the widget's appearance?",
      a: "Yes. You can configure the position, language, and auto-open behavior through the configuration object. More styling options are coming soon."
    },
    {
      q: "What languages are supported?",
      a: "The widget auto-detects the language from your message and responds in the same language. It supports English, French, Luxembourgish, and more."
    },
    {
      q: "Is the widget mobile-friendly?",
      a: "Absolutely. On mobile devices the widget opens in full-screen mode for the best user experience, and switches to a popup on desktop."
    },
    {
      q: "How do I update the widget?",
      a: "The widget is served from our CDN, so updates are automatic. You don't need to change anything on your end — new features and fixes are deployed seamlessly."
    }
  ];

  const quickSnippet = `<script>
(function(){
  var s=document.createElement('script');
  s.src='${currentOrigin}/embed.js?clientId=YOUR_CLIENT_ID&apiKey=YOUR_API_KEY&_t='+Date.now();
  s.async=true;
  document.body.appendChild(s);
})();
</script>`;
  const advancedSnippet = `<script>
  window.RMT_CHAT_CONFIG = {
    clientId: 'YOUR_CLIENT_ID',
    apiKey: 'YOUR_API_KEY',
    position: { bottom: '30px', right: '30px' },
    language: 'en',
    autoOpen: false
  };
</script>
<script>
(function(){
  var s=document.createElement('script');
  s.src='${currentOrigin}/embed.js?_t='+Date.now();
  s.async=true;
  document.body.appendChild(s);
})();
</script>`;

  const handleCopy = (text: string, type: 'quick' | 'advanced') => {
    navigator.clipboard.writeText(text);
    if (type === 'quick') {
      setCopiedQuick(true);
      setTimeout(() => setCopiedQuick(false), 2000);
    } else {
      setCopiedAdvanced(true);
      setTimeout(() => setCopiedAdvanced(false), 2000);
    }
  };

  return (
    <div className="App">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-layout">
            <div className="hero-content">
              <div className="hero-badge">
                <Zap size={14} />
                <span>AI-Powered Support</span>
              </div>
              <h1 className="hero-title">
                Integrate <span className="hero-highlight">AI Chat</span> into your website
              </h1>
              <p className="hero-subtitle">
                Add an intelligent support chatbot to any website with a single line of code. Fast, responsive, and fully customizable.
              </p>
              <div className="hero-actions">
                <a href="#integration" className="hero-btn hero-btn-primary">
                  <BookOpen size={18} />
                  <span>Get Started</span>
                </a>
                <a href="#features" className="hero-btn hero-btn-secondary">
                  <span>View Features</span>
                  <ArrowRight size={18} />
                </a>
              </div>
              <div className="hero-stats">
                <div className="hero-stat">
                  <span className="hero-stat-value">1 line</span>
                  <span className="hero-stat-label">to integrate</span>
                </div>
                <div className="hero-stat-divider"></div>
                <div className="hero-stat">
                  <span className="hero-stat-value">{'<'}1s</span>
                  <span className="hero-stat-label">load time</span>
                </div>
                <div className="hero-stat-divider"></div>
                <div className="hero-stat">
                  <span className="hero-stat-value">100%</span>
                  <span className="hero-stat-label">responsive</span>
                </div>
              </div>
            </div>
            <div className="hero-visual">
              <div className="hero-visual-card">
                <div className="hero-card-mockup">
                  <div className="mockup-header">
                    <div className="mockup-dots">
                      <span></span><span></span><span></span>
                    </div>
                  </div>
                  <div className="mockup-body">
                    <div className="mockup-line long"></div>
                    <div className="mockup-line medium"></div>
                    <div className="mockup-line short"></div>
                    <div className="mockup-line long"></div>
                    <div className="mockup-line medium"></div>
                  </div>
                </div>
                <div className="hero-floating-widget">
                  <img src={botIcon} alt="RMT-AI" className="hero-bot-icon" />
                  <span className="hero-widget-pulse"></span>
                  <span className="hero-widget-orbit-dot hero-orbit-1"></span>
                  <span className="hero-widget-orbit-dot hero-orbit-2"></span>
                </div>
                <div className="hero-chat-preview">
                  <div className="preview-msg preview-msg-bot">
                    <div className="preview-avatar">
                      <img src={botIcon} alt="" />
                    </div>
                    <div className="preview-bubble">Hi! How can I help you?</div>
                  </div>
                  <div className="preview-msg preview-msg-user">
                    <div className="preview-bubble">I need help with setup</div>
                  </div>
                </div>
              </div>
              <div className="hero-glow"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section" id="features">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why RMT-AI?</h2>
            <p className="section-subtitle">Everything you need for intelligent customer support</p>
          </div>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon icon-purple">
                <Zap size={24} />
              </div>
              <h3>Lightning Fast</h3>
              <p>Async loading ensures zero impact on your page performance. Your users won't notice a thing.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon icon-blue">
                <Code size={24} />
              </div>
              <h3>Easy Integration</h3>
              <p>A single script tag is all you need. Copy, paste, and you're live in under a minute.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon icon-orange">
                <Smartphone size={24} />
              </div>
              <h3>Fully Responsive</h3>
              <p>Looks great on any device. Full-screen on mobile, elegant popup on desktop.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="docs-section" id="integration">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Integration Guide</h2>
            <p className="section-subtitle">Get up and running in minutes</p>
          </div>

          <div className="landing-guide-steps">
            {/* Step 1 */}
            <div className="landing-guide-step">
              <div className="landing-step-number">1</div>
              <div className="landing-step-content">
                <h3>Get Your Credentials</h3>
                <p>Sign in to the <strong>RMT-AI Dashboard</strong>, create a client, and generate an API key. You'll need both your <code>clientId</code> and <code>apiKey</code>.</p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="landing-guide-step">
              <div className="landing-step-number">2</div>
              <div className="landing-step-content">
                <h3>Add Allowed Origins</h3>
                <p>In your client settings, add your website domain (e.g. <code>https://yoursite.com</code>) to the <strong>Allowed Origins</strong> list. This ensures only your site can use the widget.</p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="landing-guide-step">
              <div className="landing-step-number">3</div>
              <div className="landing-step-content">
                <h3>Quick Integration</h3>
                <p>Add this snippet before the closing <code>&lt;/body&gt;</code> tag. Replace <code>YOUR_CLIENT_ID</code> and <code>YOUR_API_KEY</code> with your actual credentials.</p>
                <div className="code-block">
                  <div className="code-block-header">
                    <span className="code-lang">HTML</span>
                    <button
                      className="copy-btn"
                      onClick={() => handleCopy(quickSnippet, 'quick')}
                    >
                      {copiedQuick ? <Check size={14} /> : <Copy size={14} />}
                      <span>{copiedQuick ? 'Copied!' : 'Copy'}</span>
                    </button>
                  </div>
                  <pre><code>{quickSnippet}</code></pre>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="landing-guide-step">
              <div className="landing-step-number">4</div>
              <div className="landing-step-content">
                <h3>Advanced Configuration (Optional)</h3>
                <p>Customize the widget position, language, and behavior with a configuration object.</p>
                <div className="code-block">
                  <div className="code-block-header">
                    <span className="code-lang">HTML</span>
                    <button
                      className="copy-btn"
                      onClick={() => handleCopy(advancedSnippet, 'advanced')}
                    >
                      {copiedAdvanced ? <Check size={14} /> : <Copy size={14} />}
                      <span>{copiedAdvanced ? 'Copied!' : 'Copy'}</span>
                    </button>
                  </div>
                  <pre><code>{advancedSnippet}</code></pre>
                </div>
              </div>
            </div>

            {/* Step 5 */}
            <div className="landing-guide-step">
              <div className="landing-step-number">5</div>
              <div className="landing-step-content">
                <h3>Test It</h3>
                <p>Open your website — the chat widget should appear at the bottom-right corner. Click it to start a conversation with your AI assistant.</p>
              </div>
            </div>
          </div>

          {/* Config Options */}
          <div className="config-card">
            <div className="config-card-header">
              <Settings size={20} />
              <h3>Configuration Options</h3>
            </div>
            <div className="config-table-wrap">
              <table className="config-table">
                <thead>
                  <tr>
                    <th>Option</th>
                    <th>Type</th>
                    <th>Default</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>clientId</code></td>
                    <td><span className="type-badge">string</span></td>
                    <td><span className="default-val">required</span></td>
                    <td>Your unique client identifier</td>
                  </tr>
                  <tr>
                    <td><code>apiKey</code></td>
                    <td><span className="type-badge">string</span></td>
                    <td><span className="default-val">required</span></td>
                    <td>Your API key for authentication</td>
                  </tr>
                  <tr>
                    <td><code>position</code></td>
                    <td><span className="type-badge">object</span></td>
                    <td><code>{`{ bottom: '30px', right: '30px' }`}</code></td>
                    <td>Widget position on screen</td>
                  </tr>
                  <tr>
                    <td><code>language</code></td>
                    <td><span className="type-badge">string</span></td>
                    <td><code>'en'</code></td>
                    <td>Widget language (en, fr)</td>
                  </tr>
                  <tr>
                    <td><code>autoOpen</code></td>
                    <td><span className="type-badge">boolean</span></td>
                    <td><code>false</code></td>
                    <td>Auto-open widget on page load</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section" id="faq">
        <div className="container">
          <div className="section-header">
            <div className="faq-header-icon">
              <HelpCircle size={24} />
            </div>
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">Everything you need to know about RMT-AI</p>
          </div>
          <div className="faq-list">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className={`faq-item ${openFaq === i ? 'is-open' : ''}`}
              >
                <button
                  className="faq-question"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span>{faq.q}</span>
                  <ChevronDown size={18} className="faq-chevron" />
                </button>
                <div className="faq-answer">
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="app-footer">
        <div className="container">
          <p>Built with RMT-AI</p>
        </div>
      </footer>

      <ChatWidget />
    </div>
  );
}
